import { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import api from '../api/axios';
import { useAuth } from '../context/AuthContext';
import DashboardLeadModal from '../components/dashboard/DashboardLeadModal';
import DashboardProjectModal from '../components/dashboard/DashboardProjectModal';
import DashboardQuoteModal from '../components/dashboard/DashboardQuoteModal';
import DashboardFollowUpModal from '../components/dashboard/DashboardFollowUpModal';
import './Dashboard.css';

const inr = (n) => Number(n || 0).toLocaleString('en-IN');
const pad = (n) => String(n).padStart(2, '0');

const getTodayStr = () => {
  const d = new Date();
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
};

function initials(name = '') {
  return (
    name
      .trim()
      .split(/\s+/)
      .map((p) => p[0])
      .join('')
      .slice(0, 2)
      .toUpperCase() || 'TS'
  );
}

function formatDateTime(value) {
  if (!value) return 'Not set';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return 'Not set';
  return d.toLocaleString('en-IN', { day: '2-digit', month: 'short', hour: 'numeric', minute: '2-digit' });
}

function cleanText(value) {
  return String(value || '')
    .replace(/<[^>]*>/g, ' ')
    .replace(/&nbsp;/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

function whatsappLink(phone) {
  const digits = String(phone || '').replace(/\D/g, '');
  if (!digits) return null;
  return `https://wa.me/${digits.length === 10 ? `91${digits}` : digits}`;
}

function EmptyState({ text }) {
  return <div className="dash-empty">{text}</div>;
}

function KpiCard({ label, value, note, tone = 'mauve' }) {
  return (
    <div className={`dash-kpi dash-kpi-${tone}`}>
      <span>{label}</span>
      <strong>{value}</strong>
      <p>{note}</p>
    </div>
  );
}

function SectionCard({ title, subtitle, children, action }) {
  return (
    <section className="dash-card">
      <div className="dash-section-head">
        <div>
          <h2>{title}</h2>
          <p>{subtitle}</p>
        </div>
        {action}
      </div>
      {children}
    </section>
  );
}

function DashboardFilterBar({
  dateRange,
  onSelectDateRange,
  startDate,
  setStartDate,
  endDate,
  setEndDate,
  assignedTo,
  setAssignedTo,
  salespersons = [],
  isSalesUser = false,
  onRefresh,
  loading,
}) {
  const pills = [
    { id: 'today', label: 'Today' },
    { id: 'yesterday', label: 'Yesterday' },
    { id: 'last7days', label: 'Last 7 Days' },
    { id: 'last30days', label: 'Last 30 Days' },
    { id: 'custom', label: 'Custom Range' },
  ];

  return (
    <div className="dash-filter-container">
      <div className="dash-filter-left">
        <div className="dash-pills-group">
          {pills.map((pill) => (
            <button
              key={pill.id}
              type="button"
              className={`dash-pill-btn ${dateRange === pill.id ? 'active' : ''}`}
              onClick={() => onSelectDateRange(pill.id)}
            >
              {pill.label}
            </button>
          ))}
        </div>

        {dateRange === 'custom' && (
          <div className="dash-custom-date-row">
            <label>From:</label>
            <input
              type="date"
              className="dash-date-input"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
            />
            <label>To:</label>
            <input
              type="date"
              className="dash-date-input"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
            />
          </div>
        )}
      </div>

      <div className="dash-filter-right">
        {!isSalesUser ? (
          <div className="dash-bd-selector-wrap">
            <label htmlFor="bd-select" className="dash-filter-label">BD / Salesperson:</label>
            <select
              id="bd-select"
              className="dash-bd-select"
              value={assignedTo}
              onChange={(e) => setAssignedTo(e.target.value)}
            >
              <option value="">All BDs / Team Members</option>
              {salespersons.map((sp) => (
                <option key={sp._id} value={sp._id}>
                  {sp.name} {sp.department ? `(${sp.department})` : sp.role ? `(${sp.role})` : ''}
                </option>
              ))}
            </select>
          </div>
        ) : (
          <div className="dash-bd-badge">
            <span>My Assigned Leads</span>
          </div>
        )}

        <button
          type="button"
          className="dash-refresh-btn"
          onClick={onRefresh}
          title="Refresh Dashboard"
          disabled={loading}
        >
          {loading ? '⟳ Refreshing...' : '⟳ Refresh'}
        </button>
      </div>
    </div>
  );
}

function FollowUpNotesFeed({ items, dateRange, onSelectDateRange }) {
  if (!items?.length) {
    return (
      <div className="dash-empty-feed">
        <p>No follow-up notes or updates recorded for this date selection.</p>
        {dateRange === 'today' && (
          <div className="dash-empty-feed-actions">
            <button
              type="button"
              className="dash-pill-btn"
              style={{ border: '1px solid var(--sand-dark)', background: '#fff' }}
              onClick={() => onSelectDateRange('yesterday')}
            >
              Check Yesterday's Updates
            </button>
            <button
              type="button"
              className="dash-pill-btn"
              style={{ border: '1px solid var(--sand-dark)', background: '#fff' }}
              onClick={() => onSelectDateRange('last7days')}
            >
              Check Last 7 Days
            </button>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="dash-feed-list">
      {items.map((item) => {
        const clientName = item.client?.name || item.project?.clientName || 'Client';
        const clientPhone = item.client?.phone || item.project?.phone;
        const stage = item.client?.stage || item.project?.stage;
        const authorName = item.createdBy?.name || 'BD';
        const wa = whatsappLink(clientPhone);
        const linkUrl = item.project?._id
          ? `/projects/${item.project._id}`
          : item.client?._id
          ? `/projects/${item.client._id}`
          : '#';

        return (
          <div className="dash-feed-item" key={item._id}>
            <div className="dash-feed-left">
              <div className="dash-avatar" title={authorName}>
                {initials(authorName)}
              </div>
            </div>
            <div className="dash-feed-center">
              <div className="dash-feed-title-row">
                <Link to={linkUrl} className="dash-feed-client-link">
                  <strong>{clientName}</strong>
                </Link>
                {stage && <span className={`badge badge-${stage}`}>{stage}</span>}
                {item.followUpCompleted && (
                  <span className="badge badge-success">✓ Follow-up Completed</span>
                )}
                {item.followUpDate && !item.followUpCompleted && (
                  <span className="badge badge-warning">Due: {formatDateTime(item.followUpDate)}</span>
                )}
              </div>
              <p className="dash-feed-desc">
                {cleanText(item.description || item.title) || 'No note details'}
              </p>
              <div className="dash-feed-meta">
                <span className="dash-feed-author">
                  By <b>{authorName}</b>
                </span>
                <span className="dash-feed-time">· {formatDateTime(item.createdAt)}</span>
              </div>
            </div>
            <div className="dash-feed-right">
              <div className="dash-action-group">
                {clientPhone && (
                  <a href={`tel:${clientPhone}`} className="dash-mini-btn dash-mini-btn-call" title="Call">
                    Call
                  </a>
                )}
                {wa && (
                  <a href={wa} target="_blank" rel="noreferrer" className="dash-mini-btn" title="WhatsApp">
                    WhatsApp
                  </a>
                )}
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}

function FollowUpList({ items, overdue = false, onComplete }) {
  if (!items?.length) {
    return <EmptyState text={overdue ? 'No overdue follow-ups. Clear desk energy.' : 'No follow-ups scheduled for today.'} />;
  }

  return (
    <div className="dash-list">
      {items.map((item) => {
        const clientName = item.client?.name || 'Client';
        const projectTitle = item.project?.title || item.project?.stage || 'General follow-up';
        const phone = item.client?.phone;
        const wa = whatsappLink(phone);
        return (
          <div className="dash-row" key={item._id}>
            <div className="dash-avatar">{initials(clientName)}</div>
            <div className="dash-row-main">
              <strong>{clientName}</strong>
              <span>{projectTitle}</span>
              <p>{item.title || item.description || 'Follow up with client'}</p>
            </div>
            <div className="dash-row-end">
              <b>{overdue ? `${item.daysOverdue || 1}d late` : formatDateTime(item.followUpDate)}</b>
              <div className="dash-action-group">
                {phone && (
                  <a href={`tel:${phone}`} className="dash-mini-btn dash-mini-btn-call" title="Call client">
                    Call
                  </a>
                )}
                {wa && (
                  <a href={wa} target="_blank" rel="noreferrer" className="dash-mini-btn" title="Open WhatsApp">
                    WhatsApp
                  </a>
                )}
                {onComplete && (
                  <button
                    type="button"
                    onClick={() => onComplete(item._id)}
                    className="dash-mini-btn dash-mini-btn-done"
                    title="Mark follow-up done"
                  >
                    ✓ Done
                  </button>
                )}
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}

function CollectionList({ items }) {
  if (!items?.length) return <EmptyState text="No pending client collections found." />;

  return (
    <div className="dash-list">
      {items.map((item) => {
        const pct = item.total > 0 ? Math.round((item.received / item.total) * 100) : 0;
        return (
          <div className="dash-collection" key={item._id}>
            <div className="dash-collection-top">
              <div>
                <strong>{item.client?.name || 'Client'}</strong>
                <span>{item.invoiceNumber}</span>
              </div>
              <b>Rs. {inr(item.pending)}</b>
            </div>
            <div className="dash-progress">
              <i style={{ width: `${Math.min(100, pct)}%` }} />
            </div>
            <div className="dash-collection-meta">
              <span>Received Rs. {inr(item.received)}</span>
              <span>{pct}% collected</span>
            </div>
          </div>
        );
      })}
    </div>
  );
}

function AlertList({ items }) {
  if (!items?.length) return <EmptyState text="No project alerts right now." />;

  return (
    <div className="dash-list">
      {items.map((item) => (
        <Link to={`/projects/${item._id}`} className="dash-alert" key={item._id}>
          <div>
            <strong>{item.title}</strong>
            <span>{item.clientName} · {item.stage || 'No stage'}</span>
          </div>
          <em className={`dash-alert-${item.tone || 'warning'}`}>{item.reason}</em>
        </Link>
      ))}
    </div>
  );
}

function SharedNotesList({ items }) {
  if (!items?.length) return <EmptyState text="No notes shared with you yet." />;

  return (
    <div className="dash-list">
      {items.map((item) => (
        <Link
          to={item.project?._id ? `/projects/${item.project._id}?tab=notes` : '/activity'}
          className="dash-shared-note"
          key={item._id}
        >
          <div className="dash-avatar">{initials(item.client?.name)}</div>
          <div>
            <strong>{item.client?.name || 'Client'}</strong>
            <span>{item.project?.title || 'Client note'} · {formatDateTime(item.createdAt)}</span>
            <p>{cleanText(item.title || item.description) || 'Note shared with you'}</p>
          </div>
        </Link>
      ))}
    </div>
  );
}

function SalesLeaderboard({ items, periodLabel = 'Period' }) {
  if (!items?.length) return <EmptyState text="No sales team performance metrics available for this period." />;

  return (
    <div className="table-wrap desktop-table-mobile-hidden tablet-table-hidden">
      <table>
        <thead>
          <tr>
            <th>Sales Executive</th>
            <th>Department</th>
            <th>Assigned Leads</th>
            <th>Follow-up Notes ({periodLabel})</th>
            <th>Visits Done ({periodLabel})</th>
            <th>Proposals Sent ({periodLabel})</th>
            <th>Deals Won ({periodLabel})</th>
            <th>Won Value ({periodLabel})</th>
          </tr>
        </thead>
        <tbody>
          {items.map((m) => (
            <tr key={m._id}>
              <td>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem' }}>
                  <div className="dash-avatar" style={{ width: 28, height: 28, fontSize: '0.68rem' }}>
                    {initials(m.name)}
                  </div>
                  <div>
                    <strong>{m.name}</strong>
                    <span style={{ fontSize: '0.72rem', color: 'var(--muted)', display: 'block' }}>{m.email}</span>
                  </div>
                </div>
              </td>
              <td><span className="badge badge-info">{m.department || 'Sales'}</span></td>
              <td><strong>{m.assignedLeads}</strong></td>
              <td><span className="badge badge-warning" style={{ fontWeight: 800 }}>{m.notesLogged || 0}</span></td>
              <td>{m.visitsDone}</td>
              <td>{m.proposalsSent}</td>
              <td><span className="badge badge-success" style={{ fontWeight: 800 }}>{m.dealsWon} Won</span></td>
              <td><strong style={{ color: 'var(--plum)' }}>Rs. {inr(m.wonValue)}</strong></td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function RecentProjects({ projects }) {
  if (!projects?.length) return <EmptyState text="No active projects yet." />;

  return (
    <div className="table-wrap desktop-table-mobile-hidden tablet-table-hidden">
      <table>
        <thead>
          <tr>
            <th>Project</th>
            <th>Client</th>
            <th>Stage</th>
            <th>Priority</th>
            <th>Completion</th>
          </tr>
        </thead>
        <tbody>
          {projects.map((p) => (
            <tr key={p._id}>
              <td>
                <Link to={`/projects/${p._id}`} style={{ color: 'var(--charcoal)', textDecoration: 'none' }}>
                  <strong>{p.title}</strong>
                </Link>
              </td>
              <td>{p.client?.name ?? 'No client'}</td>
              <td><span className={`badge badge-${p.stage}`}>{p.stage}</span></td>
              <td><span className={`badge badge-${p.priority}`}>{p.priority}</span></td>
              <td>
                <div className="progress-wrap">
                  <div className="progress-bar">
                    <div className="progress-fill" style={{ width: `${p.completionPercentage || 0}%` }} />
                  </div>
                  <span className="progress-label">{p.completionPercentage || 0}%</span>
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function StatusSnapshot({ title, subtitle, items, emptyText }) {
  const rows = (items || []).slice(0, 8);
  return (
    <SectionCard title={title} subtitle={subtitle}>
      {rows.length ? (
        <div className="dash-snapshot-list">
          {rows.map((item) => (
            <div className="dash-snapshot-row" key={item._id || item.status || item.stage}>
              <span>{item._id || item.status || item.stage || 'Other'}</span>
              <strong>{inr(item.count)}</strong>
            </div>
          ))}
        </div>
      ) : (
        <EmptyState text={emptyText} />
      )}
    </SectionCard>
  );
}

function QuickActionBar({ onAction }) {
  return (
    <div className="dash-quick-actions-bar">
      <button
        type="button"
        className="dash-quick-btn"
        onClick={() => onAction('lead')}
        title="Create a new sales lead"
      >
        <span className="dash-quick-icon">+</span>
        <span>New Lead</span>
      </button>
      <button
        type="button"
        className="dash-quick-btn"
        onClick={() => onAction('quote')}
        title="Create a client quotation"
      >
        <span className="dash-quick-icon">📄</span>
        <span>Create Quote</span>
      </button>
      <button
        type="button"
        className="dash-quick-btn"
        onClick={() => onAction('project')}
        title="Start a new client project"
      >
        <span className="dash-quick-icon">🏗️</span>
        <span>New Project</span>
      </button>
      <button
        type="button"
        className="dash-quick-btn"
        onClick={() => onAction('followup')}
        title="Log or schedule a follow-up"
      >
        <span className="dash-quick-icon">📞</span>
        <span>Add Follow-up</span>
      </button>
      <Link to="/payments" className="dash-quick-btn" title="View financial metrics and payments">
        <span className="dash-quick-icon">💰</span>
        <span>Financials</span>
      </Link>
    </div>
  );
}

function RecentLeadsTable({ leads }) {
  if (!leads?.length) return <EmptyState text="No active leads recorded for this filter." />;

  return (
    <>
      <div className="table-wrap desktop-table-mobile-hidden tablet-table-hidden">
        <table>
          <thead>
            <tr>
              <th>Lead / Client</th>
              <th>Phone</th>
              <th>Stage</th>
              <th>Est. Budget</th>
              <th>Assigned To</th>
              <th>Latest Note / Update</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {leads.map((lead) => {
              const wa = whatsappLink(lead.phone);
              return (
                <tr key={lead._id}>
                  <td>
                    <Link to={`/projects/${lead.projectId || lead._id}`} style={{ color: 'var(--charcoal)', textDecoration: 'none' }}>
                      <strong>{lead.name}</strong>
                    </Link>
                  </td>
                  <td>{lead.phone || '—'}</td>
                  <td><span className={`badge badge-${lead.stage}`}>{lead.stage}</span></td>
                  <td><strong>{lead.estBudget > 0 ? `Rs. ${inr(lead.estBudget)}` : '—'}</strong></td>
                  <td><span style={{ fontSize: '0.8rem', color: 'var(--charcoal-mid)' }}>{lead.assignedToName || 'Unassigned'}</span></td>
                  <td>
                    {lead.latestNote ? (
                      <div className="dash-lead-note-cell">
                        <span className="dash-lead-note-text" title={cleanText(lead.latestNote.description || lead.latestNote.title)}>
                          {cleanText(lead.latestNote.title || lead.latestNote.description).slice(0, 45) || 'Note added'}
                        </span>
                        <small className="dash-lead-note-meta">
                          {lead.latestNote.createdByName ? `${lead.latestNote.createdByName} · ` : ''}{formatDateTime(lead.latestNote.createdAt)}
                        </small>
                      </div>
                    ) : (
                      <span className="dash-lead-no-note">—</span>
                    )}
                  </td>
                  <td>
                    <div className="dash-action-group" style={{ justifyContent: 'flex-start' }}>
                      {lead.phone && (
                        <a href={`tel:${lead.phone}`} className="dash-mini-btn dash-mini-btn-call" title="Call">
                          Call
                        </a>
                      )}
                      {wa && (
                        <a href={wa} target="_blank" rel="noreferrer" className="dash-mini-btn" title="WhatsApp">
                          WhatsApp
                        </a>
                      )}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <div className="mobile-card-list">
        {leads.map((lead) => {
          const wa = whatsappLink(lead.phone);
          return (
            <div className="dash-row" key={lead._id}>
              <div className="dash-avatar">{initials(lead.name)}</div>
              <div className="dash-row-main">
                <Link to={`/projects/${lead.projectId || lead._id}`} style={{ color: 'var(--charcoal)', textDecoration: 'none' }}>
                  <strong>{lead.name}</strong>
                </Link>
                <span>{lead.phone || 'No phone'} · {lead.assignedToName || 'Unassigned'}</span>
                <p><span className={`badge badge-${lead.stage}`}>{lead.stage}</span> {lead.estBudget > 0 ? `· Rs. ${inr(lead.estBudget)}` : ''}</p>
                {lead.latestNote && (
                  <div className="dash-mobile-note-snippet">
                    <strong>Latest:</strong> {cleanText(lead.latestNote.title || lead.latestNote.description).slice(0, 60)}
                    <span>({lead.latestNote.createdByName} · {formatDateTime(lead.latestNote.createdAt)})</span>
                  </div>
                )}
              </div>
              <div className="dash-row-end">
                <div className="dash-action-group">
                  {lead.phone && (
                    <a href={`tel:${lead.phone}`} className="dash-mini-btn dash-mini-btn-call" title="Call">
                      Call
                    </a>
                  )}
                  {wa && (
                    <a href={wa} target="_blank" rel="noreferrer" className="dash-mini-btn" title="WhatsApp">
                      WhatsApp
                    </a>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </>
  );
}

function RecentQuotationsTable({ quotes }) {
  if (!quotes?.length) return <EmptyState text="No recent quotations found." />;

  return (
    <div className="table-wrap desktop-table-mobile-hidden tablet-table-hidden">
      <table>
        <thead>
          <tr>
            <th>Quote Code</th>
            <th>Client</th>
            <th>Project</th>
            <th>Amount</th>
            <th>Status</th>
          </tr>
        </thead>
        <tbody>
          {quotes.map((q) => (
            <tr key={q._id}>
              <td>
                <Link to={q.projectId ? `/projects/${q.projectId}?tab=quotes` : '/quotations'} style={{ color: 'var(--plum)', fontWeight: 800, textDecoration: 'none' }}>
                  {q.quoteCode}
                </Link>
              </td>
              <td><strong>{q.clientName}</strong></td>
              <td>{q.projectTitle || '—'}</td>
              <td><strong style={{ color: 'var(--plum)' }}>Rs. {inr(q.total)}</strong></td>
              <td><span className={`badge badge-${q.status}`}>{q.status}</span></td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export default function Dashboard() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  // Modal and toast states
  const [activeModal, setActiveModal] = useState(null); // 'lead' | 'project' | 'quote' | 'followup' | null
  const [toastMessage, setToastMessage] = useState(null);

  // Date and BD Filter States (Default: "today")
  const [dateRange, setDateRange] = useState('today');
  const [startDate, setStartDate] = useState(getTodayStr());
  const [endDate, setEndDate] = useState(getTodayStr());
  const [assignedTo, setAssignedTo] = useState('');
  const [salespersons, setSalespersons] = useState([]);

  const handleActionSuccess = (msg) => {
    setToastMessage(msg);
    setTimeout(() => setToastMessage(null), 4500);
    fetchDashboard();
  };

  // Fetch salespersons / BD team members for filter
  useEffect(() => {
    (async () => {
      try {
        const res = await api.get('/users', { params: { isActive: 'true' } });
        setSalespersons(res.data.data?.users || []);
      } catch (err) {
        console.error('Failed to load salespersons:', err);
      }
    })();
  }, []);

  const handleSelectDateRange = (range) => {
    setDateRange(range);
    const now = new Date();
    if (range === 'today') {
      const todayStr = getTodayStr();
      setStartDate(todayStr);
      setEndDate(todayStr);
    } else if (range === 'yesterday') {
      const y = new Date(now.getFullYear(), now.getMonth(), now.getDate() - 1);
      const yStr = `${y.getFullYear()}-${pad(y.getMonth() + 1)}-${pad(y.getDate())}`;
      setStartDate(yStr);
      setEndDate(yStr);
    } else if (range === 'last7days') {
      const s7 = new Date(now.getFullYear(), now.getMonth(), now.getDate() - 6);
      const s7Str = `${s7.getFullYear()}-${pad(s7.getMonth() + 1)}-${pad(s7.getDate())}`;
      setStartDate(s7Str);
      setEndDate(getTodayStr());
    } else if (range === 'last30days') {
      const s30 = new Date(now.getFullYear(), now.getMonth(), now.getDate() - 29);
      const s30Str = `${s30.getFullYear()}-${pad(s30.getMonth() + 1)}-${pad(s30.getDate())}`;
      setStartDate(s30Str);
      setEndDate(getTodayStr());
    }
  };

  const fetchDashboard = useCallback(() => {
    if (!user) {
      setLoading(false);
      return;
    }
    setLoading(true);
    const params = { dateRange };
    if (assignedTo) params.assignedTo = assignedTo;
    if (startDate) params.startDate = startDate;
    if (endDate) params.endDate = endDate;

    api.get('/dashboard', { params })
      .then((r) => setData(r.data.data))
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [user, dateRange, startDate, endDate, assignedTo]);

  useEffect(() => {
    fetchDashboard();
  }, [fetchDashboard]);

  const handleCompleteFollowUp = async (id) => {
    try {
      await api.patch(`/activity-logs/${id}/complete-followup`);
      fetchDashboard();
    } catch (err) {
      console.error('Failed to complete follow-up', err);
    }
  };

  const hour = new Date().getHours();
  const greeting = hour < 12 ? 'Good morning' : hour < 17 ? 'Good afternoon' : 'Good evening';

  const userRole = user?.role || 'employee';
  const userDept = user?.department || '';
  const isAdmin = ['admin', 'manager'].includes(userRole);
  const isSales = userRole === 'employee' && userDept === 'sales';
  const isDesign = userRole === 'employee' && userDept === 'design';

  const summary = data?.summary || {};
  const monitor = data?.dailyMonitor || {};
  const todayFollowUps = monitor.todayFollowUps || [];
  const overdueFollowUps = monitor.overdueFollowUps || [];
  const pendingCollections = monitor.pendingCollections || [];
  const salesLeaderboard = data?.salesLeaderboard || [];
  const recentLeads = data?.recentLeads || [];
  const recentQuotes = data?.recentQuotes || [];
  const recentProjects = data?.recentProjects || [];
  const followUpNotes = data?.followUpNotes || [];
  const pendingCollectionTotal = pendingCollections.reduce((sum, item) => sum + Number(item.pending || 0), 0);

  const dateLabelMap = {
    today: 'Today',
    yesterday: 'Yesterday',
    last7days: 'Last 7 Days',
    last30days: 'Last 30 Days',
    custom: 'Selected Period',
  };
  const periodLabel = dateLabelMap[dateRange] || 'Period';

  const selectedBdObj = salespersons.find((s) => s._id === assignedTo);
  const bdName = selectedBdObj?.name || (isSales ? user?.name : 'All Executives');

  return (
    <div className="dashboard-page slide-up">
      <div className="page-header">
        <div className="page-header-left">
          <h1>{greeting}, {user?.name?.split(' ')[0]}.</h1>
          <p>
            {isAdmin && 'Executive control room: company revenue, team performance, and pipeline health.'}
            {isSales && 'Sales workspace: your assigned leads, daily call queue, and deals won.'}
            {isDesign && 'Design workspace: your active client projects, drawings, and task load.'}
            {!isAdmin && !isSales && !isDesign && 'Your personal CRM action center.'}
          </p>
        </div>
        <button
          type="button"
          className="btn btn-primary"
          onClick={() => setActiveModal('followup')}
          title="Schedule or log a client follow-up"
        >
          + Add Follow-up
        </button>
      </div>

      {/* Quick Action Shortcuts */}
      <QuickActionBar onAction={setActiveModal} />

      {/* Dashboard Date & BD Filter Bar */}
      <DashboardFilterBar
        dateRange={dateRange}
        onSelectDateRange={handleSelectDateRange}
        startDate={startDate}
        setStartDate={setStartDate}
        endDate={endDate}
        setEndDate={setEndDate}
        assignedTo={assignedTo}
        setAssignedTo={setAssignedTo}
        salespersons={salespersons}
        isSalesUser={isSales}
        onRefresh={fetchDashboard}
        loading={loading}
      />

      {loading && !data ? (
        <div className="dash-loading"><div className="spinner spinner-lg" /></div>
      ) : (
        <>
          {/* Dynamic KPI Cards (Reflecting Selected Date Range & BD) */}
          <div className="dash-kpi-grid">
            {isAdmin && !assignedTo && (
              <>
                <KpiCard
                  label={`Won Deals (${periodLabel})`}
                  value={`Rs. ${inr(summary.periodWonValue)}`}
                  note={`${summary.periodWonDeals || 0} won deals in ${periodLabel.toLowerCase()}`}
                  tone="teal"
                />
                <KpiCard
                  label="Active Pipeline"
                  value={inr(summary.activeProjects)}
                  note="Active client projects"
                  tone="blue"
                />
                <KpiCard
                  label={`New Leads (${periodLabel})`}
                  value={inr(summary.periodLeadsCount)}
                  note={`${inr(summary.totalClients)} total active leads`}
                  tone="purple"
                />
                <KpiCard
                  label={`Activity & Notes (${periodLabel})`}
                  value={inr(summary.periodNotesCount)}
                  note="Logged follow-ups & notes"
                  tone="green"
                />
                <KpiCard
                  label="Net Revenue"
                  value={`Rs. ${inr(summary.netRevenue)}`}
                  note="Inflow minus paid expenses"
                  tone="green"
                />
                <KpiCard
                  label="Pending Receivables"
                  value={`Rs. ${inr(summary.totalReceivables)}`}
                  note="Approved & open invoices"
                  tone="amber"
                />
              </>
            )}

            {(isSales || (isAdmin && assignedTo)) && (
              <>
                <KpiCard
                  label={`Won Deals (${periodLabel})`}
                  value={`${summary.periodWonDeals || 0}`}
                  note={`Value: Rs. ${inr(summary.periodWonValue)}`}
                  tone="teal"
                />
                <KpiCard
                  label="Assigned Leads"
                  value={inr(summary.myAssignedClients || summary.totalClients)}
                  note={`${summary.periodLeadsCount || 0} created in ${periodLabel.toLowerCase()}`}
                  tone="blue"
                />
                <KpiCard
                  label={`Activity & Notes (${periodLabel})`}
                  value={inr(summary.periodNotesCount)}
                  note="Notes & completed calls"
                  tone="green"
                />
                <KpiCard
                  label="Active Projects"
                  value={inr(summary.myActiveProjects || summary.activeProjects)}
                  note={`Managing: ${bdName}`}
                  tone="purple"
                />
                <KpiCard
                  label="Pending Follow-ups"
                  value={todayFollowUps.length + overdueFollowUps.length}
                  note={`${overdueFollowUps.length} overdue`}
                  tone={overdueFollowUps.length > 0 ? 'red' : 'green'}
                />
                <KpiCard
                  label="Deals Won (Month)"
                  value={`${summary.myWonDealsMonth || 0}`}
                  note={`Rs. ${inr(summary.myWonValueMonth)} month value`}
                  tone="amber"
                />
              </>
            )}

            {isDesign && !assignedTo && (
              <>
                <KpiCard
                  label="My Design Projects"
                  value={inr(summary.myDesignProjects)}
                  note="Assigned design projects"
                  tone="purple"
                />
                <KpiCard
                  label="My Pending Tasks"
                  value={inr(summary.myPendingTasks)}
                  note="Tasks in progress / created"
                  tone="amber"
                />
                <KpiCard
                  label="Tasks Completed (Month)"
                  value={inr(summary.myCompletedTasksMonth)}
                  note="Completed this month"
                  tone="green"
                />
                <KpiCard
                  label="Pending Follow-ups"
                  value={todayFollowUps.length + overdueFollowUps.length}
                  note={`${overdueFollowUps.length} overdue`}
                  tone={overdueFollowUps.length > 0 ? 'red' : 'green'}
                />
              </>
            )}
          </div>

          {/* Follow-up & Note Updates Section + Pending Follow-up Action Queue */}
          <div className="dash-monitor-grid">
            <SectionCard
              title={`Follow-up & Note Updates (${periodLabel})`}
              subtitle={`Activity log updates and follow-ups by ${bdName} in ${periodLabel.toLowerCase()}`}
              action={
                <Link to="/activity" className="dash-link">
                  All Logs
                </Link>
              }
            >
              <FollowUpNotesFeed
                items={followUpNotes}
                dateRange={dateRange}
                onSelectDateRange={handleSelectDateRange}
              />
            </SectionCard>

            <SectionCard
              title={isSales ? "My Call & Follow-up Queue" : `Follow-up Queue (${bdName})`}
              subtitle="Scheduled calls & follow-ups requiring immediate action"
              action={<Link to="/activity" className="dash-link">View All</Link>}
            >
              {todayFollowUps.length > 0 || overdueFollowUps.length > 0 ? (
                <>
                  {overdueFollowUps.length > 0 && (
                    <div style={{ marginBottom: '1rem' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', marginBottom: '0.5rem' }}>
                        <span className="badge badge-danger" style={{ fontWeight: 800 }}>
                          {overdueFollowUps.length} Overdue
                        </span>
                        <span style={{ fontSize: '0.78rem', color: 'var(--muted)' }}>Requires urgent contact</span>
                      </div>
                      <FollowUpList items={overdueFollowUps} overdue onComplete={handleCompleteFollowUp} />
                    </div>
                  )}

                  {todayFollowUps.length > 0 && (
                    <div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', marginBottom: '0.5rem' }}>
                        <span className="badge badge-info" style={{ fontWeight: 800 }}>
                          {todayFollowUps.length} Due Today
                        </span>
                        <span style={{ fontSize: '0.78rem', color: 'var(--muted)' }}>Contact before end of day</span>
                      </div>
                      <FollowUpList items={todayFollowUps} onComplete={handleCompleteFollowUp} />
                    </div>
                  )}
                </>
              ) : (
                <EmptyState text="No pending follow-ups scheduled for today. All caught up!" />
              )}
            </SectionCard>
          </div>

          {/* Admin Sales Team Leaderboard */}
          {isAdmin && salesLeaderboard.length > 0 && (
            <SectionCard
              title="Sales Team Performance"
              subtitle={`Executive metrics filtered for ${periodLabel.toLowerCase()}: notes logged, visits, proposals, and deals won`}
              action={<Link to="/projects" className="dash-link">All Leads</Link>}
            >
              <SalesLeaderboard items={salesLeaderboard} periodLabel={periodLabel} />
            </SectionCard>
          )}

          {/* Live Recent Leads & Active Pipeline (with Latest Note) */}
          <div className="dash-monitor-grid">
            <SectionCard
              title={assignedTo ? `Leads & Inquiries (${bdName})` : `Recent Leads (${periodLabel})`}
              subtitle="Client leads with their latest follow-up note & status"
              action={<Link to="/projects" className="dash-link">View All Leads</Link>}
            >
              <RecentLeadsTable leads={recentLeads} />
            </SectionCard>

            <SectionCard
              title={`Quotations & Proposals (${periodLabel})`}
              subtitle="Client quotation documents"
              action={<Link to="/quotations" className="dash-link">All Quotes</Link>}
            >
              <RecentQuotationsTable quotes={recentQuotes} />
            </SectionCard>
          </div>

          {/* Pipeline Snapshot & Financials / Tasks */}
          <div className="dash-monitor-grid">
            <StatusSnapshot
              title={isSales || assignedTo ? `Funnel Stages (${bdName})` : "Company Pipeline Funnel"}
              subtitle="Current distribution of leads across stages"
              items={data?.projectsByStage}
              emptyText="No project stage data available for this selection."
            />

            {pendingCollections.length > 0 ? (
              <SectionCard
                title="Pending Collections"
                subtitle="Invoices where payment is still pending"
                action={<Link to="/payments" className="dash-link">Financials</Link>}
              >
                <CollectionList items={pendingCollections} />
              </SectionCard>
            ) : (
              <StatusSnapshot
                title="Task Load Snapshot"
                subtitle="Current team task breakdown"
                items={data?.tasksByStatus}
                emptyText="All tasks are completed."
              />
            )}
          </div>

          {/* Recent Active Projects */}
          <SectionCard
            title={assignedTo ? `Active Projects (${bdName})` : "Recent Active Projects"}
            subtitle="Projects in progress across the pipeline"
            action={<Link to="/projects" className="dash-link">View All Projects</Link>}
          >
            <RecentProjects projects={recentProjects} />
          </SectionCard>
        </>
      )}

      {/* Action Toast Feedback */}
      {toastMessage && (
        <div className="dash-toast slide-down">
          <span className="dash-toast-icon">✓</span>
          <span className="dash-toast-text">{toastMessage}</span>
          <button
            type="button"
            className="dash-toast-close"
            onClick={() => setToastMessage(null)}
            aria-label="Close notification"
          >
            &times;
          </button>
        </div>
      )}

      {/* Operational Dashboard Modals */}
      <DashboardLeadModal
        isOpen={activeModal === 'lead'}
        onClose={() => setActiveModal(null)}
        onSuccess={handleActionSuccess}
        salespersons={salespersons}
      />

      <DashboardProjectModal
        isOpen={activeModal === 'project'}
        onClose={() => setActiveModal(null)}
        onSuccess={handleActionSuccess}
        salespersons={salespersons}
      />

      <DashboardQuoteModal
        isOpen={activeModal === 'quote'}
        onClose={() => setActiveModal(null)}
      />

      <DashboardFollowUpModal
        isOpen={activeModal === 'followup'}
        onClose={() => setActiveModal(null)}
        onSuccess={handleActionSuccess}
        salespersons={salespersons}
      />
    </div>
  );
}
